import posthog from "posthog-js";

import { isPrivateStatusUrl } from "./private-status-route";

export const POSTHOG_BROWSER_PROXY_HOST = "/p0s7h0g";
export const POSTHOG_CONSENT_CHANGED_EVENT = "agroproj:posthog-consent-changed";

const POSTHOG_DEFAULT_APP_HOST = "https://us.posthog.com";
const POSTHOG_SERVICE_NAME = "agroproj-web";
const POSTHOG_CAPTURE_TIMEOUT_MS = 2_000;
const POSTHOG_DEBUG_STORAGE_KEY = "__agroproj_posthog_debug__";
const POSTHOG_CONSENT_STORAGE_KEY = "__agroproj_posthog_consent__";
const SAFE_PERSON_PROPERTY_KEYS = ["role", "organizationId", "organizationSlug"] as const;
const TRANSIENT_ERROR_PATTERNS = [
  "aborted",
  "aborterror",
  "cancelled",
  "canceled",
  "failed to fetch",
  "networkerror",
  "network error",
  "load failed",
  "the operation was aborted",
];

export type PostHogSafePersonProperties = Partial<
  Record<(typeof SAFE_PERSON_PROPERTY_KEYS)[number], string | number | boolean>
>;

export type PostHogCaptureContext = {
  tags?: Record<string, string | number | boolean | undefined>;
  extra?: Record<string, unknown>;
};

export type PostHogConsentState = "accepted" | "rejected" | null;

declare global {
  interface Window {
    __posthogInitialized?: boolean;
  }
}

export function getPostHogProjectKey() {
  return process.env.NEXT_PUBLIC_POSTHOG_PROJECT_TOKEN ?? process.env.NEXT_PUBLIC_POSTHOG_KEY;
}

export function getPostHogConsent(): PostHogConsentState {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    const value = localStorage.getItem(POSTHOG_CONSENT_STORAGE_KEY);
    return value === "accepted" || value === "rejected" ? value : null;
  } catch {
    return null;
  }
}

export function hasPostHogConsent() {
  return (
    !(typeof window !== "undefined" && isPrivateStatusUrl(window.location.pathname)) &&
    getPostHogConsent() === "accepted"
  );
}

export function setPostHogConsent(consent: Exclude<PostHogConsentState, null>) {
  if (typeof window === "undefined") {
    return;
  }

  try {
    localStorage.setItem(POSTHOG_CONSENT_STORAGE_KEY, consent);
    window.dispatchEvent(new CustomEvent(POSTHOG_CONSENT_CHANGED_EVENT, { detail: consent }));
  } catch {
    // Local storage may be unavailable in restricted browser contexts.
  }
}

export function getPostHogAppHost() {
  return process.env.NEXT_PUBLIC_POSTHOG_APP_HOST ?? POSTHOG_DEFAULT_APP_HOST;
}

function getPostHogEnvironment() {
  return process.env.NEXT_PUBLIC_POSTHOG_ENVIRONMENT ?? process.env.NODE_ENV ?? "development";
}

function isPostHogDebugEnabled() {
  if (typeof window === "undefined") {
    return false;
  }

  try {
    return localStorage.getItem(POSTHOG_DEBUG_STORAGE_KEY) === "true";
  } catch {
    return false;
  }
}

function debugPostHog(label: string, payload?: unknown) {
  if (!isPostHogDebugEnabled()) {
    return;
  }

  console.debug("[PostHog]", label, payload);
}

export function applyPostHogDebugQueryParam() {
  if (typeof window === "undefined") {
    return;
  }

  const debugParam = new URLSearchParams(window.location.search).get("posthog_debug");
  if (debugParam !== "true" && debugParam !== "1" && debugParam !== "false" && debugParam !== "0") {
    return;
  }

  try {
    if (debugParam === "true" || debugParam === "1") {
      localStorage.setItem(POSTHOG_DEBUG_STORAGE_KEY, "true");
      console.debug("[PostHog] Debug mode enabled");
    } else {
      localStorage.removeItem(POSTHOG_DEBUG_STORAGE_KEY);
      console.debug("[PostHog] Debug mode disabled");
    }
  } catch {
    // Local storage may be unavailable in restricted browser contexts.
  }
}

function normalizeError(error: unknown): Error {
  if (error instanceof Error) {
    return error;
  }

  if (typeof error === "string") {
    return new Error(error);
  }

  if (error && typeof error === "object" && "message" in error) {
    const normalized = new Error(
      typeof error.message === "string" ? error.message : String(error.message),
    );

    if ("name" in error && typeof error.name === "string") {
      normalized.name = error.name;
    }

    if ("stack" in error && typeof error.stack === "string") {
      normalized.stack = error.stack;
    }

    return normalized;
  }

  return new Error(String(error));
}

export function shouldFilterPostHogException(error: unknown) {
  const normalizedError = normalizeError(error);
  const name = normalizedError.name.toLowerCase();
  const message = normalizedError.message.toLowerCase();

  if (name === "aborterror") {
    return true;
  }

  if (message === "next_redirect" || message === "next_not_found") {
    return true;
  }

  return TRANSIENT_ERROR_PATTERNS.some((pattern) => message.includes(pattern));
}

function buildProperties(context?: PostHogCaptureContext) {
  const properties: Record<string, unknown> = {
    environment: getPostHogEnvironment(),
    service: POSTHOG_SERVICE_NAME,
    source: "browser",
  };

  if (context?.tags) {
    for (const [key, value] of Object.entries(context.tags)) {
      if (value !== undefined) {
        properties[`tag_${key}`] = value;
      }
    }
  }

  if (context?.extra && Object.keys(context.extra).length > 0) {
    properties.context = context.extra;
  }

  return Object.keys(properties).length > 0 ? properties : undefined;
}

async function fetchPostHogCapture(pathname: string, payload: unknown) {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), POSTHOG_CAPTURE_TIMEOUT_MS);

  try {
    const response = await fetch(`${POSTHOG_BROWSER_PROXY_HOST}${pathname}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
      signal: controller.signal,
    });

    if (!response.ok) {
      console.error("[PostHog] Browser fallback capture failed", {
        status: response.status,
        statusText: response.statusText,
      });
    }
  } catch (error) {
    console.error("[PostHog] Browser fallback capture request failed", { error });
  } finally {
    clearTimeout(timeout);
  }
}

export function captureClientException(error: unknown, context?: PostHogCaptureContext) {
  if (!hasPostHogConsent()) {
    debugPostHog("exception-skipped-no-consent", context);
    return;
  }

  if (shouldFilterPostHogException(error)) {
    debugPostHog("exception-filtered", error);
    return;
  }

  if (typeof window !== "undefined" && window.__posthogInitialized !== true) {
    const apiKey = getPostHogProjectKey();
    if (!apiKey) {
      return;
    }

    const normalizedError = normalizeError(error);
    debugPostHog("exception-browser-fallback", {
      message: normalizedError.message,
      context,
    });
    void fetchPostHogCapture("/capture/", {
      api_key: apiKey,
      event: "$exception",
      distinct_id: "browser:nextjs",
      properties: {
        ...buildProperties(context),
        $exception_level: "error",
        $exception_message: normalizedError.message,
        $exception_source: "web",
        $exception_stack_trace_raw: normalizedError.stack ?? "",
        $exception_type: normalizedError.name || "Error",
      },
    });
    return;
  }

  debugPostHog("exception", context);
  posthog.captureException(error, buildProperties(context));
}

export function capturePosthogEvent(name: `AgroProj:${string}:${string}`, properties = {}) {
  if (!hasPostHogConsent()) {
    debugPostHog("capture-skipped-no-consent", { name, properties });
    return;
  }

  debugPostHog("capture", { name, properties });
  posthog.capture(name, {
    ...properties,
    environment: getPostHogEnvironment(),
    service: POSTHOG_SERVICE_NAME,
    source: "browser",
  });
}

export function identifyPosthogUser(distinctId: string, properties?: PostHogSafePersonProperties) {
  if (!hasPostHogConsent()) {
    debugPostHog("identify-skipped-no-consent", { distinctId, properties });
    return;
  }

  debugPostHog("identify", { distinctId, properties });
  posthog.identify(distinctId, properties);
}
